import React from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import smartlookClient from 'smartlook-client';

import { makeStyles, Theme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';

import { openOrCloseGlobalDialog } from '../../action/dialog/global-dialog';

import { GLOBAL_DIALOGS } from '../../dialogs/global-dialogs-config';
import { AUTOMATION_TRIGGER_TYPES } from '../../start-date-triggered-communication/automation-trigger-types';
import { orgAutomationsRoute } from '../../web-routes';
import * as WebRoutes from '../../web-routes';

import { isProd } from '../../config/config';
import { MaterialDesignIcon } from '../../util/material-design-icons';

type InputProps = {
  orgId: number;
  criticalDate: CriticalDate;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    openApplicantProfileDialog: (jobApplicantId: number) =>
      dispatch(
        openOrCloseGlobalDialog(GLOBAL_DIALOGS.APPLICANT_DIALOG.key, true, { jobApplicantId }),
      ),
    openEmployeeProfileDialog: (employeeId: number) =>
      dispatch(
        openOrCloseGlobalDialog(GLOBAL_DIALOGS.EMPLOYEE_DIALOG.key, true, {
          employeeId,
          orgId,
        }),
      ),
  },
});
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps & RouteComponentProps;

const useStyles = makeStyles((theme: Theme) => ({
  arrow: { color: '#e0e0e0' },
  tooltip: {
    backgroundColor: '#e0e0e0',
    maxWidth: 200,
    color: theme.palette.common.black,
    fontSize: 14,
  },
  calendarEvent: {
    backgroundColor: (theme as any).palette.primary[700],
    '&:hover': {
      backgroundColor: (theme as any).palette.primary[400],
      transition: '250ms all',
    },
  },
  disabledCalendarEvent: {
    backgroundColor: (theme as any).palette.grey[500],
    '&:hover': {
      backgroundColor: (theme as any).palette.grey[400],
      transition: '250ms all',
    },
  },
}));

export const CriticalDateEventPill = withRouter(
  connect(
    null,
    mapDispatchToProps,
  )((props: Props) => {
    const {
      orgId,
      criticalDate,
      history,
      actions,
      location: { pathname },
    } = props;
    const classes = useStyles();
    const onFullCalendarView = pathname.includes(WebRoutes.eventCalendarRoute(orgId));

    const smartlookClickDisabledAutomation = {
      eventName: 'Event Click From Calendar',
      props: {
        eventType: `${criticalDate.eventType}${
          criticalDate.communicationType ? `_${criticalDate.communicationType.toUpperCase()}` : ''
        }`,
        isEventEnabled: Boolean(criticalDate.communicationEnabled),
        startDateTriggeredCommunicationId: criticalDate.triggeredCommunicationId || '',
        source: onFullCalendarView ? 'Full Calendar View' : 'Dashboard Schedule',
      },
    };

    const handleDateClick = (event: CriticalDate) => () => {
      if (isProd && smartlookClient) {
        smartlookClient.track(
          smartlookClickDisabledAutomation.eventName,
          smartlookClickDisabledAutomation.props,
        );
      }

      if (!event.communicationEnabled) {
        history.push({
          pathname: orgAutomationsRoute(orgId),
          search: `?focus=${event.triggeredCommunicationId}`.concat(
            event.eventType === AUTOMATION_TRIGGER_TYPES.BIRTHDATE ? `&tab=${event.eventType}` : '',
          ),
        });
      } else if (event.jobApplicantId) {
        actions.openApplicantProfileDialog(event.jobApplicantId);
      } else if (event.employeeId) {
        actions.openEmployeeProfileDialog(event.employeeId);
      }
    };

    const formatTime = (event: CriticalDate) => {
      if (event.allDay) {
        return '';
      }
      return `${moment(event.startAt).format('h:mma')}${
        event.endAt ? ` - ${moment(event.endAt).format('h:mma')}` : ''
      } - `;
    };

    const Icon = () => {
      let iconName = null;
      if (criticalDate.communicationType === 'survey') {
        iconName = 'forum';
      } else if (criticalDate.communicationType === 'message') {
        iconName = 'sms';
      } else if (
        criticalDate.communicationType === 'alert' ||
        criticalDate.eventType === 'APPLICANT_REACH_OUT'
      ) {
        iconName = 'email';
      } else if (criticalDate.eventType === 'INTERVIEW') {
        iconName = 'account_box';
      } else if (criticalDate.eventType === 'PHONE_INTERVIEW') {
        iconName = 'call';
      } else if (criticalDate.eventType === 'NEW_EMPLOYEE_START') {
        iconName = 'person_add';
      }

      if (iconName) {
        return (
          <div className="u-display-flex" style={{ height: '1rem' }}>
            <MaterialDesignIcon
              iconName={iconName}
              className="u-margin-right u-margin-left-xs u-alignSelf-center"
              size="xs"
            />
          </div>
        );
      }

      return null;
    };

    return (
      <Tooltip
        placement="right"
        arrow
        classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
        title={criticalDate.description || ''}
        className="u-cursor-pointer"
        key={criticalDate.description || new Date().toString()}
      >
        <div
          onClick={handleDateClick(criticalDate)}
          role="presentation"
          className={`${
            criticalDate.communicationEnabled
              ? classes.calendarEvent
              : classes.disabledCalendarEvent
          } u-margin-top-xs u-cursor-pointer u-color-white u-borderRadius-3 u-padding-xs typo-subtitle u-display-flex u-alignItems-flexStart`}
        >
          <Icon />
          <div>
            {formatTime(criticalDate)}
            {criticalDate.title}
          </div>
        </div>
      </Tooltip>
    );
  }),
);
